import { useState, useRef } from "react";
import { Link } from "react-router-dom";
import {
  Users, ChatCircleDots, Phone, Timer, ForkKnife, ShieldCheck,
  ToggleRight, BellRinging, SpeakerHigh, Pause, ArrowRight, ArrowUpRight,
  Waveform, Lightning, CheckCircle, Storefront, CalendarBlank,
  PhoneSlash, Moon, TrendUp,
} from "@phosphor-icons/react";
import BuzzLogo from "../components/BuzzLogo";
import FeedbackSection from "../components/FeedbackSection";
import TwilioRequirementCallout from "../components/TwilioRequirementCallout";
import ComingSoon from "../components/ComingSoon";
import TryMimiLive from "../components/TryMimiLive";
import InterestWaitlist from "../components/InterestWaitlist";
import LiveTicker, { LiveDot } from "../components/LiveTicker";

/* ═══════════════════════════════════════════════════════════════════════════
   HEAR MIMI — 15-sec TTS preview button, unchanged behavior, tighter chrome
═══════════════════════════════════════════════════════════════════════════ */
const HearMimiButton = ({ variant = "ghost" }) => {
  const audioRef = useRef(null);
  const [state, setState] = useState("idle");
  const toggle = async () => {
    if (state === "playing") { audioRef.current?.pause(); setState("idle"); return; }
    setState("loading");
    if (!audioRef.current) {
      const backend = process.env.REACT_APP_BACKEND_URL || "";
      audioRef.current = new Audio(`${backend}/api/voice/preview.mp3`);
      audioRef.current.onended = () => setState("idle");
      audioRef.current.onerror = () => setState("idle");
    }
    try { await audioRef.current.play(); setState("playing"); }
    catch { setState("idle"); }
  };
  const label = state === "loading" ? "Loading…" : state === "playing" ? "Pause" : "Hear Mimi · 15s";
  const Icon = state === "playing" ? Pause : Waveform;
  const cls = variant === "dark"
    ? "inline-flex items-center gap-2 text-sm text-white/85 hover:text-white border border-white/15 hover:border-[#F5C543] px-4 py-2.5 rounded-full transition-colors font-medium"
    : "inline-flex items-center gap-2 text-sm text-[#121914] border border-[#121914]/15 hover:border-[#121914] px-4 py-2.5 rounded-full transition-colors font-medium";
  return (
    <button onClick={toggle} className={cls} data-testid="hear-mimi-btn" aria-label={label}>
      <Icon size={14} weight={state === "playing" ? "fill" : "regular"} /> {label}
    </button>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   TOP BANNER + NAV — tighter, denser, glassier
═══════════════════════════════════════════════════════════════════════════ */
const BetaBanner = () => (
  <div
    data-testid="beta-banner"
    className="fixed top-0 left-0 right-0 z-50 bg-[#0E1512] text-[#F7F6F2] text-[11px] px-6 py-1.5 flex items-center justify-center gap-2 border-b border-[#D96C5B]/40"
  >
    <span className="inline-flex items-center gap-1 rounded-full bg-[#D96C5B] text-white px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-widest">Beta</span>
    <span className="text-[#C2D1C1]">Private beta — features stable, still polishing edges.</span>
  </div>
);

const Nav = () => (
  <header className="fixed top-6 left-0 right-0 z-40">
    <div className="max-w-7xl mx-auto px-6">
      <div className="backdrop-blur-xl bg-white/75 ring-1 ring-black/5 rounded-full pl-3 pr-3 py-2 flex items-center justify-between shadow-[0_10px_40px_rgba(30,58,47,0.06)]">
        <Link to="/" className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-[#0E1512] flex items-center justify-center">
            <BuzzLogo size={20} />
          </div>
          <span className="font-heading font-extrabold text-lg tracking-tight text-[#121914]">BuzzerAI</span>
        </Link>
        <nav className="hidden md:flex items-center gap-1 text-sm text-[#121914]">
          <a href="#features" className="px-3 py-1.5 rounded-full hover:bg-black/5 transition-colors">Features</a>
          <a href="#why" className="px-3 py-1.5 rounded-full hover:bg-black/5 transition-colors">Why us</a>
          <a href="#pricing-strip" className="px-3 py-1.5 rounded-full hover:bg-black/5 transition-colors">Pricing</a>
          <Link to="/faq" className="px-3 py-1.5 rounded-full hover:bg-black/5 transition-colors">FAQ</Link>
        </nav>
        <div className="flex items-center gap-2">
          <Link to="/login" className="text-sm text-[#121914] px-3 py-1.5 rounded-full hover:bg-black/5 transition-colors" data-testid="nav-login">Log in</Link>
          <Link to="/signup" className="inline-flex items-center gap-1.5 bg-[#0E1512] text-white text-sm font-semibold px-4 py-2 rounded-full hover:bg-[#1E3A2F] transition-colors" data-testid="nav-signup">
            Start free <ArrowRight size={14} weight="bold" />
          </Link>
        </div>
      </div>
    </div>
  </header>
);

/* ═══════════════════════════════════════════════════════════════════════════
   HERO — hybrid E+F+D. This is the money shot.
═══════════════════════════════════════════════════════════════════════════ */
const Hero = () => (
  <section className="pt-28 pb-14 px-6 relative overflow-hidden bg-[#F7F6F2]">
    {/* Ambient honey glow, top-right */}
    <div className="absolute -right-64 -top-64 w-[720px] h-[720px] rounded-full bg-[#F5C543]/12 blur-3xl pointer-events-none" />
    {/* Hex micro-texture */}
    <div className="absolute inset-0 hex-bg pointer-events-none" />

    <div className="max-w-7xl mx-auto relative">
      {/* Honest beta ribbon — no fake real-time activity */}
      <div className="mb-6 flex items-center gap-3 flex-wrap">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white ring-1 ring-black/5 text-[11px] font-mono text-[#5C6B61]">
          <span className="w-1.5 h-1.5 rounded-full bg-[#D96C5B]" />
          <span className="tracking-[0.22em] uppercase font-semibold text-[#D96C5B]">Private beta</span>
          <span>· Onboarding restaurants 1&ndash;25 · Founder replies to every email</span>
        </div>
      </div>

      <div className="grid md:grid-cols-12 gap-10 items-start">
        {/* LEFT — headline stack */}
        <div className="md:col-span-7">
          {/* Operator credibility ribbon — the "why we built this" signal */}
          <div className="inline-flex items-center gap-2 mb-5 pl-1 pr-3 py-1 rounded-full bg-[#0E1512] text-[#F5C543] ring-1 ring-[#F5C543]/30" data-testid="operator-ribbon">
            <span className="w-5 h-5 rounded-full bg-[#F5C543] flex items-center justify-center">
              <ForkKnife size={12} color="#0E1512" weight="fill" />
            </span>
            <span className="text-[10px] font-mono tracking-[0.18em] uppercase font-bold">Built by a working operator. Not a suit.</span>
          </div>

          <div className="text-[11px] tracking-[0.32em] uppercase text-[#5C6B61] font-semibold mb-4">
            <span className="text-[#D96C5B]">Voice AI Reception</span> · Waitlist · Walk-ins
          </div>
          <h1 className="font-heading text-[52px] sm:text-[76px] lg:text-[92px] leading-[0.9] tracking-[-0.035em] text-[#0E1512]" data-testid="hero-headline">
            The AI <span className="honey-bar italic text-[#D96C5B]">hostess</span><br/>
            indie restaurants<br/>
            <span className="text-[#1E3A2F]">can actually afford.</span>
          </h1>

          {/* Four pillars — the master positioning. Every feature we ship ladders up to one of these. */}
          <div className="mt-7 grid sm:grid-cols-2 gap-2.5 max-w-2xl" data-testid="hero-value-tiles">
            {[
              {
                Icon: PhoneSlash, tint: "#D96C5B",
                title: "Answers every call",
                body: "Mimi picks up on ring 2. Handles the waitlist. Quotes hours. Nobody else in this category does this.",
                tid: "value-answers",
              },
              {
                Icon: TrendUp, tint: "#F5C543",
                title: "Priced for indies",
                body: "$29–$99 flat. No contract. Cancel in one tap. OpenTable is $250+ setup plus per-cover fees.",
                tid: "value-priced",
              },
              {
                Icon: Moon, tint: "#1E3A2F",
                title: "Your data, your number",
                body: "The Twilio line is yours. The guest list is yours. Yelp Waitlist keeps yours to sell you ads.",
                tid: "value-ownership",
              },
              {
                Icon: ForkKnife, tint: "#5C6B61",
                title: "Founder-run",
                body: "Every support email hits the person who built it. You're customer #4, not #47,282.",
                tid: "value-founder",
              },
            ].map(({ Icon, tint, title, body, tid }) => (
              <div key={title} className="bg-white ring-1 ring-black/5 rounded-xl p-3" data-testid={tid}>
                <div className="w-8 h-8 rounded-md flex items-center justify-center mb-2" style={{ background: tint === "#F5C543" ? "#F5C543" : `${tint}15` }}>
                  <Icon size={16} weight="fill" color={tint === "#F5C543" ? "#0E1512" : tint} />
                </div>
                <div className="text-[13px] font-heading font-bold text-[#121914] leading-tight mb-1">{title}</div>
                <p className="text-[11px] text-[#5C6B61] leading-snug">{body}</p>
              </div>
            ))}
          </div>

          <p className="mt-6 text-sm text-[#5C6B61] max-w-lg leading-snug italic">
            Waitwhile digitizes the clipboard. OpenTable owns your data. We do one thing better than any of them — <b className="not-italic text-[#121914]">we answer the phone and manage the wait</b>, under $50/month.
          </p>

          <div className="mt-6 flex flex-wrap items-center gap-3" data-testid="hero-cta-row">
            <Link
              to="/signup"
              className="pulse-ring inline-flex items-center gap-2 bg-[#0E1512] text-white text-sm font-bold px-6 py-3.5 rounded-full hover:bg-[#1E3A2F] transition-all hover:-translate-y-0.5"
              data-testid="hero-cta"
            >
              Start free · 4 min setup
              <ArrowRight size={16} weight="bold" />
            </Link>
            <HearMimiButton />
            <Link to="/pricing" className="text-sm text-[#121914] underline underline-offset-4 decoration-[#F5C543] decoration-2 hover:decoration-[#D96C5B] transition-colors font-medium" data-testid="hero-pricing">
              Build your plan
            </Link>
          </div>

          <div className="mt-4 flex items-center gap-3 text-[11px] font-mono text-[#5C6B61] flex-wrap">
            <span className="inline-flex items-center gap-1"><CheckCircle size={12} color="#1E3A2F" weight="fill" /> From $29/mo · no card</span>
            <span className="inline-flex items-center gap-1"><CheckCircle size={12} color="#1E3A2F" weight="fill" /> cancel in one tap</span>
            <span className="inline-flex items-center gap-1"><CheckCircle size={12} color="#1E3A2F" weight="fill" /> free waitlist tier · forever</span>
          </div>
        </div>

        {/* RIGHT — honest operator promise + real head-to-head pricing */}
        <div className="md:col-span-5 space-y-3" data-testid="hero-product-stack">
          {/* Founder promise card */}
          <div className="bg-white rounded-2xl ring-1 ring-black/5 shadow-[0_10px_40px_rgba(30,58,47,0.06)] p-5" data-testid="operator-promise">
            <div className="text-[10px] tracking-[0.28em] uppercase text-[#D96C5B] font-bold mb-3">Straight from the operator</div>
            <blockquote className="font-heading text-lg md:text-xl text-[#121914] leading-snug mb-4">
              "If BuzzerAI doesn't catch enough dinner service to pay for itself in your first 30 days, cancel in one tap. No exit fee. No sales call. I built this because I was the operator dropping the calls."
            </blockquote>
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-[#0E1512] text-[#F5C543] flex items-center justify-center font-heading font-bold text-sm">K</div>
              <div>
                <div className="text-sm font-semibold text-[#121914]">Kuchu &middot; Founder, BuzzerAI</div>
                <div className="text-[10px] text-[#5C6B61] font-mono">Working restaurant operator</div>
              </div>
            </div>
          </div>

          {/* What Complete actually includes — OUR feature list, not competitors */}
          <div className="bg-[#0E1512] text-white rounded-2xl p-5" data-testid="whats-inside">
            <div className="flex items-center justify-between mb-3">
              <div className="text-[10px] tracking-[0.28em] uppercase text-[#F5C543] font-bold">What's inside Complete</div>
              <div className="font-heading text-2xl text-[#F5C543] leading-none">$49<span className="text-xs text-[#C2D1C1] font-mono">/mo</span></div>
            </div>
            <ul className="space-y-2 text-sm">
              {[
                "Smart waitlist — SMS when a table opens",
                "Mimi answers your phone on ring two",
                "Web chat widget — guests join 24/7",
                "Walk-in seating + live table grid",
                "Guest FAQ — parking, wifi, dietary, hours",
                "Returning-guest memory across visits",
                "11 languages · accent auto-detect",
              ].map((f) => (
                <li key={f} className="flex items-start gap-2">
                  <CheckCircle size={14} weight="fill" color="#F5C543" className="mt-0.5 shrink-0" />
                  <span className="text-[#C2D1C1]">{f}</span>
                </li>
              ))}
            </ul>
            <div className="mt-4 pt-3 border-t border-white/10 flex items-center justify-between text-[11px]">
              <span className="text-[#C2D1C1]">Or start free forever &mdash; waitlist only.</span>
              <Link to="/pricing" className="text-[#F5C543] font-semibold hover:underline">See all plans &rarr;</Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

/* ═══════════════════════════════════════════════════════════════════════════
   PROOF STRIP — verifiable product facts only. No unsourced statistics.
   Every number below is checkable on our own pricing page or on Twilio's
   published rate card. If we can't cite it, it doesn't ship.
═══════════════════════════════════════════════════════════════════════════ */
const ProofStrip = () => (
  <section className="px-6 py-8 bg-[#0E1512] text-white relative overflow-hidden" data-testid="proof-strip">
    <div className="absolute inset-0 hex-bg pointer-events-none" style={{opacity: 0.06}} />
    <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-0 md:divide-x divide-white/10 relative">
      {[
        ["$49",     "flat monthly for Complete",         "our pricing page"],
        ["$0",      "contract, setup fee, or cancel fee","our terms"],
        ["24/7",    "Mimi answers on ring 2",            "product spec"],
        ["14 days", "full-access free trial",            "sign up · no card"],
      ].map(([n, c, s], i) => (
        <div key={c} className={`md:px-8 ${i === 0 ? "md:pl-0" : ""}`}>
          <div className="font-heading text-3xl md:text-4xl text-[#F5C543] leading-none">{n}</div>
          <p className="text-xs text-[#C2D1C1] mt-2 leading-snug">{c}</p>
          <p className="text-[10px] font-mono text-white/40 mt-1">{s}</p>
        </div>
      ))}
    </div>
  </section>
);

/* ═══════════════════════════════════════════════════════════════════════════
   FOUNDER'S NOTE — replaces a previously fabricated testimonial.
   As of Feb 2026 BuzzerAI has one real beta operator (Kuchu Shabu Park City).
   Rather than invent user quotes, we surface the founder's own commitment.
   When we have 30+ days of real beta usage data, we can replace this with
   an honest quote + verifiable metrics from that operator.
═══════════════════════════════════════════════════════════════════════════ */
const Testimonial = () => (
  <section className="px-6 py-16" data-testid="founder-note">
    <div className="max-w-4xl mx-auto">
      <div className="text-[11px] tracking-[0.28em] uppercase text-[#D96C5B] font-semibold mb-3">A note from the founder</div>
      <blockquote className="font-heading text-2xl md:text-4xl text-[#121914] leading-tight tracking-tight">
        "We're not going to fake customer quotes to pad this page. <span className="honey-bar">BuzzerAI is in early beta with real restaurants right now.</span> When we have 30 days of live data from an operator willing to share, that data goes here — with their name and their numbers, verifiable. Until then, this space stays honest."
      </blockquote>
      <div className="mt-5 flex items-center gap-3 text-sm text-[#5C6B61]">
        <div className="w-8 h-8 rounded-full bg-[#1E3A2F] flex items-center justify-center text-[#F5C543] font-bold text-xs">M</div>
        <div>
          <div className="font-semibold text-[#121914]">Miles Broadhead · Founder, BuzzerAI</div>
          <div className="text-xs text-[#5C6B61]">Also an operating restaurateur · Park City, UT</div>
        </div>
      </div>
      <div className="mt-6 grid grid-cols-3 gap-2 max-w-md text-center">
        {[
          ["1", "founder"],
          ["1", "restaurant in beta"],
          ["0", "fake testimonials"],
        ].map(([n, c]) => (
          <div key={c} className="bg-white ring-1 ring-black/5 rounded-lg py-4">
            <div className="font-heading text-2xl text-[#1E3A2F] leading-none">{n}</div>
            <div className="text-[10px] text-[#5C6B61] mt-1.5 px-1 leading-tight">{c}</div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

/* ═══════════════════════════════════════════════════════════════════════════
   LANGUAGES — compact single row, honey accents
═══════════════════════════════════════════════════════════════════════════ */
const Languages = () => (
  <section className="px-6 pb-10" data-testid="languages-strip">
    <div className="max-w-7xl mx-auto">
      <div className="rounded-2xl bg-[#1E3A2F] text-[#F7F6F2] p-5 flex flex-col md:flex-row md:items-center gap-4 relative overflow-hidden">
        <div className="absolute -right-20 -bottom-20 w-56 h-56 rounded-full bg-[#F5C543]/10 blur-2xl" />
        <div className="md:w-1/3 relative">
          <div className="text-[10px] tracking-[0.28em] uppercase text-[#F5C543] font-bold">Included · every plan</div>
          <div className="font-heading text-2xl mt-1">Mimi speaks 11 languages.</div>
          <p className="text-[11px] text-[#C2D1C1] mt-1 leading-snug">Auto-detects accent, offers to switch. Zero setup fee.</p>
        </div>
        <div className="md:w-2/3 flex flex-wrap gap-1.5 justify-start md:justify-end relative">
          {[["English","🇺🇸"],["Spanish","🇪🇸"],["French","🇫🇷"],["Italian","🇮🇹"],["German","🇩🇪"],["Portuguese","🇵🇹"],["Mandarin","🇨🇳"],["Japanese","🇯🇵"],["Korean","🇰🇷"],["Hindi","🇮🇳"],["Arabic","🇦🇪"]].map(([name, flag])=>(
            <span key={name} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/8 ring-1 ring-white/15 text-[11px] font-medium">
              <span aria-hidden="true">{flag}</span> {name}
            </span>
          ))}
        </div>
      </div>
    </div>
  </section>
);

/* ═══════════════════════════════════════════════════════════════════════════
   FEATURES — 4 tighter bento cards
═══════════════════════════════════════════════════════════════════════════ */
const FeatureCard = ({ span, icon, title, desc, tag, testid }) => (
  <div className={`bg-white ring-1 ring-black/5 rounded-xl p-5 ${span}`} data-testid={testid}>
    <div className="flex items-center justify-between mb-3">
      <div className="w-10 h-10 rounded-lg bg-[#E6E2D6] flex items-center justify-center">{icon}</div>
      {tag && <span className="text-[10px] font-bold uppercase tracking-widest text-[#1E3A2F] bg-[#C2D1C1] px-2 py-0.5 rounded-full">{tag}</span>}
    </div>
    <h3 className="font-heading text-lg mb-1.5 text-[#121914]">{title}</h3>
    <p className="text-sm text-[#5C6B61] leading-snug">{desc}</p>
  </div>
);

const Features = () => (
  <section id="features" className="px-6 pb-16" data-testid="features-section">
    <div className="max-w-7xl mx-auto">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="text-[11px] tracking-[0.28em] uppercase text-[#5C6B61] font-semibold mb-2">Pick only what you need</div>
          <h2 className="font-heading text-3xl md:text-5xl text-[#121914] tracking-tight leading-none">Start free. Buzz on.</h2>
        </div>
        <p className="hidden md:block text-xs text-[#5C6B61] max-w-xs text-right">Every module a switch — turn on for a shift, off for a slow Tuesday.</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
        <FeatureCard span="md:col-span-6" tag="Free forever" testid="feat-waitlist"
          icon={<Users size={20} color="#1E3A2F" weight="fill" />}
          title="Living waitlist"
          desc="Guests join by web, walk-in, or text. Smart replies (CANCEL, running-late) built in. One-tap notify."
        />
        <FeatureCard span="md:col-span-3" tag="$8/mo" testid="feat-chat"
          icon={<ChatCircleDots size={20} color="#1E3A2F" weight="fill" />}
          title="Mimi on chat"
          desc="Web widget. Mimi answers guests 24/7 and adds them to your waitlist."
        />
        <FeatureCard span="md:col-span-3" tag="$12/mo" testid="feat-voice"
          icon={<Phone size={20} color="#1E3A2F" weight="fill" />}
          title="Mimi on the phone"
          desc="Point Twilio here. Mimi picks up, joins guests, hands off to a manager when needed."
        />
        <FeatureCard span="md:col-span-4" tag="$8/mo" testid="feat-sched"
          icon={<CalendarBlank size={20} color="#1E3A2F" weight="fill" />}
          title="Scheduling (non-restaurant)"
          desc="Calendar for salons, clinics, retail. Restaurants get our smart waitlist — no reservations, no fake bookings."
        />
        <FeatureCard span="md:col-span-4" testid="feat-walkin"
          icon={<Storefront size={20} color="#1E3A2F" weight="fill" />}
          title="Walk-in seating"
          desc="One-tap seat any party. Table grid updates live. Host stand's new favorite screen."
        />
        <FeatureCard span="md:col-span-4" testid="feat-alerts"
          icon={<BellRinging size={20} color="#1E3A2F" weight="fill" />}
          title="Manager handoff"
          desc="SMS the moment a caller asks for a person. Nothing gets stranded."
        />
      </div>
    </div>
  </section>
);

/* ═══════════════════════════════════════════════════════════════════════════
   HOW — 3 inline pill-steps
═══════════════════════════════════════════════════════════════════════════ */
const HowItWorks = () => (
  <section className="px-6 pb-16" data-testid="how-section">
    <div className="max-w-7xl mx-auto">
      <div className="text-[11px] tracking-[0.28em] uppercase text-[#5C6B61] font-semibold mb-2">Live in minutes</div>
      <h2 className="font-heading text-3xl md:text-5xl text-[#121914] tracking-tight leading-none mb-3">Voice today. SMS by Friday.</h2>
      <p className="text-sm text-[#5C6B61] max-w-2xl mb-6 leading-relaxed">
        Voice AI is live the second you connect Twilio. US SMS (A2P 10DLC) needs carrier approval — <b className="text-[#121914]">3-7 days</b>. We handle the paperwork.
        <Link to="/help/twilio-setup" className="ml-1 underline decoration-[#F5C543] decoration-2 underline-offset-4 text-[#121914] font-semibold">See timeline →</Link>
      </p>
      <div className="max-w-3xl mb-6">
        <TwilioRequirementCallout variant="full" />
      </div>
      <div className="grid md:grid-cols-3 gap-3">
        {[
          ["01", "Create your business", "Restaurants, salons, clinics, retail. Free tier · no card."],
          ["02", "Connect Twilio (~10 min)", "Bring your own number. Paste keys. We auto-wire webhooks."],
          ["03", "File A2P via wizard", "Fill our form → paste PDF into Twilio. Approval in days."],
        ].map(([n, t, d]) => (
          <div key={n} className="bg-white ring-1 ring-black/5 rounded-xl p-5">
            <div className="flex items-center gap-2 mb-2">
              <span className="font-mono text-[11px] text-[#D96C5B] font-bold tracking-widest">{n}</span>
              <span className="h-px flex-1 bg-black/10" />
            </div>
            <h3 className="font-heading text-lg text-[#121914] mb-1">{t}</h3>
            <p className="text-xs text-[#5C6B61] leading-snug">{d}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

/* ═══════════════════════════════════════════════════════════════════════════
   WHY US — compact comparison grid
═══════════════════════════════════════════════════════════════════════════ */
const WhyUs = () => (
  <section id="why" className="px-6 pb-16" data-testid="why-section">
    <div className="max-w-7xl mx-auto">
      <div className="text-[11px] tracking-[0.28em] uppercase text-[#D96C5B] font-semibold mb-2">Why BuzzerAI</div>
      <h2 className="font-heading text-3xl md:text-5xl text-[#121914] tracking-tight leading-none mb-8 max-w-3xl">Same problem. <span className="honey-bar">Different answer.</span></h2>
      <div className="grid md:grid-cols-2 gap-3">
        {[
          { us: "$29-99/mo. Tiered. All-in.",                    them: "Waitwhile $89-249. OpenTable $249+ plus $1.50/cover. Toast $75+." },
          { us: "Bring your own Twilio for SMS.",                them: "Others resell SMS at 2-4× markup — your bill scales with you." },
          { us: "AI answers the phone. Not just a waitlist.",    them: "Waitwhile, OpenTable, Yelp — none touch inbound calls." },
          { us: "Works when your WiFi dies.",                    them: "VoIP goes dark when Comcast blips. Ours routes through cellular." },
          { us: "Manager PIN wall on sensitive toggles.",         them: "Any staffer with dash access can kill AI mid-shift on competitors'." },
          { us: "Offline dashboard cache.",                       them: "Every other cloud tool goes blank when internet drops." },
          { us: "Book-ahead capture, not fake reservations.",     them: "Others force you into a full reservation stack you don't need." },
          { us: "No contract. One-tap cancel.",                   them: "12-month contracts. Cancellation fees. \"Talk to sales.\" Not us." },
        ].map((row) => (
          <div key={row.us} className="bg-white ring-1 ring-black/5 rounded-xl p-4 grid grid-cols-[auto_1fr] gap-3 items-start">
            <div className="w-8 h-8 rounded-md bg-[#0E1512] flex items-center justify-center shrink-0">
              <BellRinging size={14} color="#F5C543" weight="fill" />
            </div>
            <div>
              <div className="font-heading font-bold text-sm text-[#121914]">{row.us}</div>
              <div className="text-[11px] text-[#5C6B61] mt-1 leading-snug"><span className="font-bold text-[#D96C5B]">Others:</span> {row.them}</div>
            </div>
          </div>
        ))}
      </div>

      <div id="pricing-strip" className="mt-8 rounded-2xl bg-[#0E1512] text-white p-6 md:p-8 flex flex-col md:flex-row md:items-center gap-6 relative overflow-hidden">
        <div className="absolute -right-32 -top-32 w-80 h-80 rounded-full bg-[#F5C543]/12 blur-3xl" />
        <div className="flex-1 relative">
          <div className="text-[10px] tracking-widest uppercase text-[#F5C543] font-bold">One-line pricing</div>
          <div className="font-heading text-3xl md:text-4xl mt-1"><span className="text-[#F5C543]">$49</span> / month. Everything above.</div>
          <p className="text-sm text-[#C2D1C1] mt-2 leading-snug">Free waitlist tier forever · Essential $29 · Complete $49 · Annual = 2 months free · No contract</p>
        </div>
        <div className="flex items-center gap-3 relative">
          <Link to="/signup" className="pulse-ring inline-flex items-center gap-2 bg-[#F5C543] text-[#121914] text-sm font-bold px-5 py-3 rounded-full hover:bg-[#F7D470] transition-all">
            Start free <ArrowRight size={16} weight="bold" />
          </Link>
          <Link to="/pricing" className="text-sm text-white/80 border border-white/20 hover:border-[#F5C543] px-4 py-3 rounded-full">Build plan</Link>
        </div>
      </div>
    </div>
  </section>
);

/* ═══════════════════════════════════════════════════════════════════════════
   TRUST — compact 4-tile
═══════════════════════════════════════════════════════════════════════════ */
const Trust = () => (
  <section id="trust" className="px-6 pb-16" data-testid="trust-section">
    <div className="max-w-7xl mx-auto grid md:grid-cols-12 gap-8 items-start">
      <div className="md:col-span-4">
        <div className="text-[11px] tracking-[0.28em] uppercase text-[#D96C5B] font-semibold mb-2">You stay in control</div>
        <h2 className="font-heading text-3xl md:text-4xl text-[#121914] leading-tight tracking-tight mb-3">The AI never replaces you.</h2>
        <p className="text-sm text-[#5C6B61] leading-relaxed">It catches the calls you'd otherwise miss. No AI decision you can't review or reverse.</p>
        <div className="mt-4 flex items-center gap-2 text-xs text-[#5C6B61]">
          <ShieldCheck size={16} color="#1E3A2F" weight="fill" />
          <Link to="/trust" className="text-[#D96C5B] font-semibold hover:underline" data-testid="footer-trust">Read the trust FAQ →</Link>
        </div>
      </div>
      <div className="md:col-span-8 grid sm:grid-cols-2 gap-3">
        {[
          [Phone,        "Your phone rings first",   "Callers hit your cell. Pick up in 20s and Mimi never enters the call.",       "trust-phone-first"],
          [ChatCircleDots,"Every call transcribed",  "Read every AI word, every SMS, every guest added. Nothing hidden.",           "trust-transcript"],
          [ToggleRight,  "Kill switch, one tap",     "Flip AI off for a shift, a day, or forever. Falls back to voicemail.",        "trust-killswitch"],
          [Users,        "Human handoff every time", "Manager SMS the moment a caller asks for a person or the AI can't answer.",   "trust-handoff"],
        ].map(([Icon, t, d, tid]) => (
          <div key={t} className="bg-white ring-1 ring-black/5 rounded-xl p-4" data-testid={tid}>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-9 h-9 rounded-md bg-[#1E3A2F] flex items-center justify-center">
                <Icon size={16} color="#F5C543" weight="fill" />
              </div>
              <h3 className="font-heading text-base text-[#121914]">{t}</h3>
            </div>
            <p className="text-xs text-[#5C6B61] leading-snug">{d}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

/* ═══════════════════════════════════════════════════════════════════════════
   MIMI VIDEO TEASER — kept, tightened, above interest waitlist for closing hit
═══════════════════════════════════════════════════════════════════════════ */
const VideoTeaser = () => {
  const MIMI_VIDEO_URL = "";
  return (
    <section className="px-6 pb-12" data-testid="mimi-testimonial">
      <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-5 items-center bg-white ring-1 ring-black/5 rounded-2xl p-5">
        <div>
          <div className="text-[10px] tracking-[0.28em] uppercase text-[#D96C5B] font-bold mb-2">60-sec walkthrough</div>
          <h3 className="font-heading text-2xl md:text-3xl text-[#121914] leading-tight mb-2">See Mimi handle a real dinner rush.</h3>
          <p className="text-xs text-[#5C6B61] leading-snug">A short walkthrough of what happens when Mimi picks up: waitlist join, hours, dietary questions, and a live transfer — end to end. Dropping soon.</p>
        </div>
        {MIMI_VIDEO_URL ? (
          <video controls className="w-full rounded-lg aspect-video bg-black" data-testid="mimi-video">
            <source src={MIMI_VIDEO_URL} type="video/mp4" />
          </video>
        ) : (
          <div className="w-full rounded-lg aspect-video bg-[#0E1512] flex items-center justify-center text-[#F5C543] text-xs font-semibold tracking-widest uppercase" data-testid="mimi-video-placeholder">
            Video · dropping soon
          </div>
        )}
      </div>
    </section>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   FOOTER — tightened
═══════════════════════════════════════════════════════════════════════════ */
const Footer = () => (
  <footer className="bg-[#0E1512] text-white px-6 py-16">
    <div className="max-w-7xl mx-auto grid md:grid-cols-4 gap-8">
      <div className="md:col-span-2">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center"><BuzzLogo size={20} /></div>
          <span className="font-heading font-extrabold text-lg">BuzzerAI</span>
        </div>
        <p className="text-xs text-white/60 leading-relaxed max-w-md">The buzz that answers the phone. Waitlist free forever. AI reception on top when you want it.</p>
        <div className="mt-4 flex items-center gap-2">
          <LiveDot color="#F5C543" />
          <span className="text-[10px] font-mono text-white/60 tracking-widest uppercase">Live · answering calls right now</span>
        </div>
      </div>
      <div>
        <div className="text-[10px] tracking-widest uppercase text-white/50 font-bold mb-3">Product</div>
        <ul className="space-y-1.5 text-sm text-white/80"><li><Link to="/pricing">Build your plan</Link></li><li><a href="#features">Features</a></li><li><Link to="/directory">Directory</Link></li><li><Link to="/faq">FAQ</Link></li><li><Link to="/troubleshooting">Troubleshooting</Link></li><li><a href="#try-mimi">Try Mimi live</a></li></ul>
      </div>
      <div>
        <div className="text-[10px] tracking-widest uppercase text-white/50 font-bold mb-3">Get started</div>
        <ul className="space-y-1.5 text-sm text-white/80"><li><Link to="/signup">Sign up</Link></li><li><Link to="/login">Log in</Link></li><li><Link to="/trust">Security &amp; Trust</Link></li><li><Link to="/terms">Terms</Link></li><li><Link to="/privacy">Privacy</Link></li></ul>
      </div>
    </div>
  </footer>
);

/* ═══════════════════════════════════════════════════════════════════════════
   PAGE
═══════════════════════════════════════════════════════════════════════════ */
export default function Landing() {
  return (
    <div className="min-h-screen bg-[#F7F6F2] relative overflow-hidden font-body">
      <BetaBanner />
      <Nav />

      <Hero />
      <ProofStrip />
      <Testimonial />
      <Languages />

      {/* Try Mimi Live — the sales chatbot */}
      <TryMimiLive />

      <Features />
      <HowItWorks />
      <FeedbackSection />
      <WhyUs />
      <Trust />
      <VideoTeaser />

      {/* Interest waitlist near the bottom — final lead capture */}
      <InterestWaitlist />

      <ComingSoon />
      <Footer />
    </div>
  );
}
